import {useAllUnitsQuery} from '@transactions/graphql';
import * as React from 'react';
import {
  currencyLabelWithUnits,
  formatMoneyWithUnits,
} from 'src/lib/unitDisplay';
import type {FullUnitFragment} from '@transactions/graphql';
import type Money from 'common/Money';
import type {
  UnitsById} from 'src/lib/unitDisplay';

/**
 * All custom units keyed by id (the token stored in `currency` fields).
 * The collection is tiny and Apollo's default cache-first policy keeps it
 * to one fetch per session; renders before the query resolves fall back to
 * code-style rendering and correct themselves when it lands.
 */
export const useUnitsById = (): UnitsById => {
  const {data} = useAllUnitsQuery();
  return React.useMemo(() => {
    const map = new Map<string, FullUnitFragment>();
    for (const unit of data?.allUnits ?? []) {
      map.set(unit._id, unit);
    }
    return map;
  }, [data]);
};

/**
 * Whether the units query has settled (either data arrived or the fetch
 * failed). Amount inputs use this to hold off entry in a unit currency until
 * its displayDecimals is known — typing under the provisional 2-decimal
 * fallback would store a mis-scaled amount. On error this reports true so a
 * failed fetch degrades to the fallback instead of a permanently disabled
 * input.
 */
export const useUnitsSettled = (): boolean => {
  const {loading} = useAllUnitsQuery();
  return !loading;
};

type RenderOptions = {
  showCurrency?: boolean;
};

/**
 * Unit-aware replacement for Money.toDecimalString: renders custom-unit
 * amounts as "3 passes", delegates everything else to Money.toDecimalString.
 */
export const useMoneyFormatter = (): ((
  money: Money,
  options?: RenderOptions,
) => string) => {
  const unitsById = useUnitsById();
  return React.useCallback(
    (money: Money, options?: RenderOptions) =>
      formatMoneyWithUnits(money, unitsById, options),
    [unitsById],
  );
};

/**
 * Human label for a currency slot: unit name for custom units, the code
 * verbatim otherwise.
 */
export const useCurrencyLabel = (): ((currency: string) => string) => {
  const unitsById = useUnitsById();
  return React.useCallback(
    (currency: string) => currencyLabelWithUnits(currency, unitsById),
    [unitsById],
  );
};
